"use client";

import { useContext } from "react";
import { MacaronCardActiveContext } from "./scope";
import { GAP } from "./tokens";
import type { MacaronRenderContext, RecordAny } from "./types";

function childIds(children: unknown): string[] {
  if (!children || typeof children !== "object") return [];
  const explicit = (children as RecordAny).explicitList;
  return Array.isArray(explicit) ? explicit.filter((id): id is string => typeof id === "string") : [];
}

function mainJustify(distribution?: string): string {
  switch (distribution) {
    case "center":
      return "justify-center";
    case "end":
      return "justify-end";
    case "spaceBetween":
      return "justify-between";
    case "spaceAround":
      return "justify-around";
    case "spaceEvenly":
      return "justify-evenly";
    default:
      return "justify-start";
  }
}

function crossAlign(alignment?: string): string {
  switch (alignment) {
    case "center":
      return "items-center";
    case "end":
      return "items-end";
    case "stretch":
      return "items-stretch";
    case "baseline":
      return "items-baseline";
    default:
      return "items-stretch";
  }
}

export function MacaronColumn({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const ids = childIds(config.children);
  const inCard = useContext(MacaronCardActiveContext);
  const distribution = typeof config.distribution === "string" ? config.distribution : "";
  const alignment = typeof config.alignment === "string" ? config.alignment : "";
  const isTightTextStack = ids.length > 1 && ids.every((id) => {
    const type = ctx.getComponentType?.(id);
    return type === "Label" || type === "TagText";
  });
  const gap = isTightTextStack ? GAP.textCluster : inCard ? GAP.cardColumn : GAP.rowDefault;

  const inner = (
    <div
      className={`flex min-w-0 flex-col ${mainJustify(distribution)} ${crossAlign(alignment)}`}
      style={gap ? { gap } : undefined}
    >
      {ids.map((id) => (
        <div key={id} className="min-w-0">
          {ctx.renderChild(id)}
        </div>
      ))}
    </div>
  );

  if (inCard) {
    return <MacaronCardActiveContext.Provider value={false}>{inner}</MacaronCardActiveContext.Provider>;
  }
  return inner;
}
